import React from 'react';
import { Container, Typography, Box, Link, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AboutPage: React.FC = () => {
  const { isAuthenticated } = useAuth();
  return (
    <Container maxWidth="lg" sx={{ pt: 4, mt: 2, pb: 6 }}>
      <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" sx={{ mb: 3 }}>
        Welcome to EcoActionAI
      </Typography>

      <Typography variant="body1" paragraph>
        CSIRO EcoActionAI is a proof-of-concept tool that helps you explore climate-related risk topics within Australia. Ask about climate within Australia, regulatory reporting requirements (e.g. AASB S2), and how your organisation or industry might respond. It’s designed for quick upskilling – helping you navigate a rapidly changing climate and business landscape.
      </Typography>

      <Box sx={{ mt: 2, mb: 4 }}>
        <Button
          component={RouterLink}
          to={isAuthenticated ? '/chat-kb2' : '/login?returnUrl=/chat-kb2'}
          variant="contained"
          size="large"
        >
          Start the Chat
        </Button>
      </Box>

      <Typography variant="body1" paragraph>
        Here’s how this tool uses AI and how it’s different from general AI tools:
      </Typography>
      <Box component="ul" sx={{ pl: 3, mb: 3 }}>
        <Typography variant="body1" component="li" sx={{ mb: 2 }}>
          Your question is directed to one of several curated knowledge bases, built from credible, publicly available sources – not the entire Internet.
        </Typography>
        <Typography variant="body1" component="li">
          A large language model turns that into a response designed to be concise and fairly presented. It will always give you an answer, but don’t expect simple or definitive ones as these are genuinely complex topics.
        </Typography>
      </Box>

      <Typography variant="h6" component="h2" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
        Important disclaimers
      </Typography>
      <Box component="ul" sx={{ pl: 3, mb: 3 }}>
        <Typography variant="body1" component="li" sx={{ mb: 2 }}>
          This is a prototype product and is intended for demonstration purposes only.
        </Typography>
        <Typography variant="body1" component="li" sx={{ mb: 2 }}>
          In using EcoActionAI, you agree that CSIRO is not liable or otherwise responsible for any decision or action taken based on information presented.
        </Typography>
        <Typography variant="body1" component="li" sx={{ mb: 2 }}>
          CSIRO makes no warranties regarding the accuracy, completeness, or usefulness of any information contained or referenced in EcoActionAI.
        </Typography>
        <Typography variant="body1" component="li">
          By continuing, you agree to the terms of the <Link href="https://www.csiro.au/Privacy" target="_blank" rel="noopener noreferrer">CSIRO privacy policy</Link>.
        </Typography>
      </Box>

      <Typography variant="h6" component="h2" gutterBottom fontWeight="bold" sx={{ mt: 3 }}>
        Development context and contact information
      </Typography>
      <Typography variant="body1" paragraph>
        This tool began with motivation and funding from the <Link href="https://research.csiro.au/ri/the-future-of-climate-risk-disclosure-reporting-using-design-led-approaches-to-consider-sociotechnical-solutions-for-improving-claim-integrity/" target="_blank" rel="noopener noreferrer">CSIRO Responsible Innovation Future Science Platform (2024-2025)</Link> where a multidisciplinary research team asked, <em>‘How might incoming Generative AI and climate disclosure requirements intersect, and how do we avoid a ‘tick the box’ approach to generating yet-another-report?’</em>
      </Typography>
      <Typography variant="body1" paragraph>
        During the pilot, we learned about the disconnected knowledge gaps that needed to be urgently filled, saw a fast-moving AI-market quickly become crowded, and detected a strong interest in analysing submitted reports for different purposes. The team is working towards a research pipeline across these areas.
      </Typography>
      <Typography variant="body1" paragraph>
        The current version focuses on a single feature: trustworthy guidance drawn from a growing base of quality public sources. We’ve built in guardrails and design patterns to support transparency and explainability of how AI-responses are generated. Information is delivered in a way meant to be easily absorbed and entertain genuine engagement.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>CSIRO Project team:</strong> Cara Stitzlein, Haohui Chen, David Evans and SukYee Yong.
      </Typography>
      <Typography variant="body1">
        <strong>Contact:</strong> <Link href="mailto:Supports-ecoactionai@csiro.au">Supports-ecoactionai@csiro.au</Link>.
      </Typography>
    </Container>
  );
};

export default AboutPage;
